import { createAction } from '@reduxjs/toolkit'

import { NewOrganization } from 'api/Cerebro/orgScoped/organizationApi'
import { PaginatedResponse } from 'api/Cerebro/types'
import { UserSettings } from 'api/Cerebro/unscoped/user'
import { SignupData } from 'helpers/params'
import {
    Brand,
    Integration,
    Organization,
    OrganizationInvitation,
    ProfilePermission,
    User,
    UserPermission,
} from 'types'
import { Tokens } from 'types/resources/auth'

interface AuthErrorPayload {
    error: string
}

interface EmailPasswordPayload {
    email: string
    password: string
}

// Reset Auth
export const resetAuthErrors = createAction('RESET_AUTH_ERRORS')

// Load Auth

export interface LoadAuthFinishPayload {
    displayDowntimePage?: boolean
    displayMaintenancePage?: boolean
}

export const loadAuthRequest = createAction('LOAD_AUTH_REQUEST')
export const loadAuthFinish =
    createAction<LoadAuthFinishPayload>('LOAD_AUTH_FINISH')
export const loadAuthFailure =
    createAction<AuthErrorPayload>('LOAD_AUTH_FAILURE')

// Fetch organization integration status
export const fetchLatestOrganizationIntegrationSuccess = createAction<
    PaginatedResponse<Integration>
>('FETCH_LATEST_ORGANIZATION_INTEGRATION_SUCCESS')

// Fetch organization ad integrations

export const fetchLatestOrganizationAdIntegrationsSuccess = createAction<
    PaginatedResponse<Brand>
>('FETCH_LATEST_ORGANIZATION_AD_INTEGRATIONS_SUCCESS')

// Change organization
interface ChangeOrganizationPayload {
    organizationId: string
    redirect?: string
    tab?: string
    filters?: Record<string, unknown>
    resources?: unknown
}

export const changeOrganizationRequest =
    createAction<ChangeOrganizationPayload>('CHANGE_ORGANIZATION_REQUEST')
export const changeOrganizationFailure = createAction<AuthErrorPayload>(
    'CHANGE_ORGANIZATION_FAILURE'
)
export const setOrganizationId = createAction<{ organizationId: string }>(
    'SET_ORGANIZATION_ID'
)

// Change organization group
interface ChangeOrganizationGroupPayload {
    organizationId: string
    organizationGroupId: string
    redirect?: string
    tab?: string
    resources?: unknown
}

export const changeOrganizationGroupRequest =
    createAction<ChangeOrganizationGroupPayload>(
        'CHANGE_ORGANIZATION_GROUP_REQUEST'
    )
export const changeOrganizationGroupFailure = createAction<AuthErrorPayload>(
    'CHANGE_ORGANIZATION_GROUP_FAILURE'
)

// Fetch user settings
export const fetchUserSettingsSuccess = createAction<UserSettings>(
    'FETCH_USER_SETTINGS_SUCCESS'
)

// Save user feature permissions
export const fetchUserFeaturePermissionsSuccess = createAction<
    UserPermission[]
>('FETCH_USER_FEATURE_PERMISSIONS_SUCCESS')

// Change user organization feature permissions
export const fetchUserOrganizationFeaturePermissionsSuccess = createAction<
    UserPermission[]
>('FETCH_USER_ORGANIZATION_FEATURE_PERMISSIONS_SUCCESS')

// Sign Up
export const signUpRequest = createAction<SignupData>('SIGN_UP_REQUEST')
export const signUpSuccess =
    createAction<EmailPasswordPayload>('SIGN_UP_SUCCESS')
export const signUpFailure = createAction<AuthErrorPayload>('SIGN_UP_FAILURE')

// Sign In
export const signInRequest =
    createAction<EmailPasswordPayload>('SIGN_IN_REQUEST')

interface SignInMfaRequestPayload extends EmailPasswordPayload {
    tokens: Tokens
    code: string
}

export const signInMfaRequest = createAction<SignInMfaRequestPayload>(
    'SIGN_IN_MFA_REQUEST'
)
export const signInMfaRequired = createAction('SIGN_IN_MFA_REQUIRED')

export const resendCodeRequest = createAction<EmailPasswordPayload>(
    'RESEND_CODE_REQUEST'
)
export const resendCodeSuccess = createAction('RESEND_CODE_SUCCESS')

export interface SignInSuccessPayload {
    currentUser: User
    organizationId: string | null | undefined
    organizationGroupId: string | null | undefined
}

export const signInSuccess =
    createAction<SignInSuccessPayload>('SIGN_IN_SUCCESS')
export const signInFailure = createAction<AuthErrorPayload>('SIGN_IN_FAILURE')

// Sign Out
export const signOutRequest = createAction('SIGN_OUT_REQUEST')
export const signOutSuccess = createAction('SIGN_OUT_SUCCESS')
export const signOutFailure = createAction<AuthErrorPayload>('SIGN_OUT_FAILURE')

// Send reset email
export const sendResetEmailRequest = createAction<{ email: string }>(
    'SEND_RESET_EMAIL_REQUEST'
)
export const sendResetEmailSuccess = createAction('SEND_RESET_EMAIL_SUCCESS')
export const sendResetEmailFailure = createAction<AuthErrorPayload>(
    'SEND_RESET_EMAIL_FAILURE'
)

// Reset password
export const resetPasswordRequest = createAction<{
    email: string
    code: string
    newPassword: string
}>('RESET_PASSWORD_REQUEST')
export const resetPasswordFailure = createAction<AuthErrorPayload>(
    'RESET_PASSWORD_FAILURE'
)

// Create Organization
export const createAuthPageOrganizationRequest = createAction<NewOrganization>(
    'CREATE_AUTH_PAGE_ORGANIZATION_REQUEST'
)
export const createAuthPageOrganizationSuccess = createAction<Organization>(
    'CREATE_AUTH_PAGE_ORGANIZATION_SUCCESS'
)
export const createAuthPageOrganizationFailure = createAction<AuthErrorPayload>(
    'CREATE_AUTH_PAGE_ORGANIZATION_FAILURE'
)

// Verify Email Address
export const verifyEmailRequest = createAction<{ code: string }>(
    'VERIFY_EMAIL_REQUEST'
)
export const verifyEmailSuccess = createAction('VERIFY_EMAIL_SUCCESS')
export const verifyEmailFailure = createAction<AuthErrorPayload>(
    'VERIFY_EMAIL_FAILURE'
)

// Confirm Account
export const resendSignUpCodeSuccess = createAction<EmailPasswordPayload>(
    'RESEND_SIGN_UP_CODE_SUCCESS'
)

// Invitation
export const getSignUpFormInvitationSuccess =
    createAction<OrganizationInvitation>('GET_SIGN_UP_FORM_INVITATION_SUCCESS')

export const acceptSignUpFormInvitationRequest = createAction<{
    invitationId: string
}>('ACCEPT_SIGN_UP_FORM_INVITATION_REQUEST')
export const acceptSignUpFormInvitationSuccess = createAction(
    'ACCEPT_SIGN_UP_FORM_INVITATION_SUCCESS'
)
export const acceptSignUpFormInvitationFailure = createAction<AuthErrorPayload>(
    'ACCEPT_SIGN_UP_FORM_INVITATION_FAILURE'
)

// profile feature permissions
export const fetchProfileFeaturePermissionsSuccess = createAction<{
    profileId: string
    permissions: ProfilePermission[]
}>('FETCH_PROFILE_FEATURE_PERMISSIONS_SUCCESS')

// pending invitations
export const fetchPendingInvitationsSuccess = createAction<
    PaginatedResponse<OrganizationInvitation>
>('FETCH_PENDING_INVITATIONS_SUCCESS')
export const acceptInvitationRequest = createAction<{ invitationId: string }>(
    'ACCEPT_INVITATION_REQUEST'
)
export const acceptInvitationSuccess = createAction('ACCEPT_INVITATION_SUCCESS')
export const acceptInvitationFailure = createAction<AuthErrorPayload>(
    'ACCEPT_INVITATION_FAILURE'
)

// Impersonation
export const setUserIsImpersonating = createAction<boolean>(
    'SET_USER_IS_IMPERSONATING'
)
export const impersonationModeFailure = createAction<AuthErrorPayload>(
    'IMPERSONATION_MODE_FAILURE'
)
